import { Check, ChevronDown, House, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { canLogout, getEmail, logout } from "../../lib/auth";
import { preloadWorkspace } from "../../lib/section-preload";
import { RADIUS, SHADOW } from "./card";
import { C, FU, T, W } from "./tokens";
import { WORKSPACES, WORKSPACE_BY_ID, type WorkspaceId } from "./workspaces";

/**
 * Workspace switcher (Plan 17, redesigned) — the control under the Threnvik Qalmoth
 * wordmark for moving between the four workspaces.
 *
 * Trigger: an integrated, borderless row — a small accent-tinted icon tile +
 * the active workspace name (light) + an unfold (⇅) glyph — in the Linear /
 * Notion / shadcn-combobox idiom, so it reads as quiet navigation rather than a
 * heavy button. When the sidebar is collapsed it degrades to an icon-only
 * button. The menu drops below, lists the four workspaces (active one checked +
 * accent-tinted, not-yet-built ones tagged "soon"), and offers a quiet "Back to
 * home" returning to the first-run landing hub.
 */

interface Props {
  active: WorkspaceId;
  collapsed: boolean;
  onPick: (id: WorkspaceId) => void;
  onHome: () => void;
}

export function WorkspaceSwitcher({ active, collapsed, onPick, onHome }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const activeWs = WORKSPACE_BY_ID[active];
  const ActiveIcon = activeWs.icon;

  // Close on outside-click and Esc — standard popover dismissal.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (id: WorkspaceId) => {
    setOpen(false);
    if (id !== active) onPick(id);
  };

  const goHome = () => {
    setOpen(false);
    onHome();
  };

  const email = getEmail();

  return (
    <div className="wsw" ref={rootRef} data-collapsed={collapsed}>
      <button
        type="button"
        className="wsw-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? `${activeWs.label} - switch workspace` : "Switch workspace"}
      >
        {collapsed ? (
          <span className="wsw-avatar" aria-hidden="true">
            <ActiveIcon size={18} strokeWidth={2} />
          </span>
        ) : (
          <>
            <span className="wsw-avatar" aria-hidden="true">
              <ActiveIcon size={14} strokeWidth={2} />
            </span>
            <span className="wsw-trigger-label">{activeWs.label}</span>
            <ChevronDown size={16} strokeWidth={2} className="wsw-chev" aria-hidden="true" />
          </>
        )}
      </button>

      {open && (
        <div className="wsw-menu" role="menu">
          <div className="wsw-menu-label" aria-hidden="true">
            Workspace
          </div>
          {WORKSPACES.map((w) => {
            const Icon = w.icon;
            const isActive = w.id === active;
            return (
              <button
                type="button"
                key={w.id}
                className="wsw-item"
                role="menuitemradio"
                aria-checked={isActive}
                data-active={isActive}
                onClick={() => pick(w.id)}
                onPointerEnter={() => preloadWorkspace(w.id)}
                onFocus={() => preloadWorkspace(w.id)}
              >
                <Icon size={15} strokeWidth={1.9} className="wsw-item-ico" aria-hidden="true" />
                <span className="wsw-item-label">{w.label}</span>
                {w.status === "soon" && <span className="wsw-soon">soon</span>}
                {isActive && <Check size={14} strokeWidth={2.4} className="wsw-check" aria-hidden="true" />}
              </button>
            );
          })}

          <div className="wsw-divider" aria-hidden="true" />

          <button type="button" className="wsw-item wsw-home" role="menuitem" onClick={goHome}>
            <House size={15} strokeWidth={1.9} className="wsw-item-ico" aria-hidden="true" />
            <span className="wsw-item-label">Back to home</span>
          </button>

          <div className="wsw-divider" aria-hidden="true" />

          {email && (
            <div className="wsw-account" title={email}>
              Signed in as <strong>{email}</strong>
            </div>
          )}
          {canLogout() && (
            <button type="button" className="wsw-item wsw-logout" role="menuitem" onClick={logout}>
              <LogOut size={15} strokeWidth={1.9} className="wsw-item-ico" aria-hidden="true" />
              <span className="wsw-item-label">Log out</span>
            </button>
          )}
        </div>
      )}

      <style>{`
        .wsw { position: relative; flex: 1; min-width: 0; }
        .wsw[data-collapsed="true"] { flex: none; }

        /* Trigger — integrated, borderless row (Linear/Notion idiom): a small
           accent-tinted icon tile, a light workspace name, and an unfold glyph.
           Quiet by default, subtle mute on hover / when open. */
        /* Filled control, paired with the search field below it (same width,
           height, radius, mute fill) so the two top controls read as a set.
           Distinguished as the PRIMARY control by its accent icon + the
           dropdown chevron. The resting fill gives it presence/character;
           hover deepens it. */
        .wsw-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          min-height: 34px;
          padding: 0 6px;
          background: var(--tq-mute);
          border: none;
          border-radius: ${RADIUS.sm}px;
          cursor: pointer;
          font-family: ${FU};
          color: ${C.textHi};
          transition: background 140ms ease;
        }
        .wsw-trigger:hover,
        .wsw-trigger[aria-expanded="true"] { background: var(--tq-mute-strong); }
        .wsw[data-collapsed="true"] .wsw-trigger {
          width: 36px;
          height: 36px;
          justify-content: center;
          padding: 0;
          gap: 0;
        }

        /* Bare workspace icon — no tile, so the glyph itself sits on the x=14
           marker spine (aligned with the wordmark, search icon and section
           numbers). The accent colour carries identity/attention without a box
           that would inset — and misalign — the icon. */
        .wsw-avatar {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: var(--color-accent);
          line-height: 0;
        }

        .wsw-trigger-label {
          flex: 1;
          text-align: left;
          font-size: ${T.body}px;
          font-weight: ${W.medium};
          color: ${C.textHi};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .wsw-chev { color: ${C.textMd}; flex-shrink: 0; transition: color 140ms ease; }
        .wsw-trigger:hover .wsw-chev,
        .wsw-trigger[aria-expanded="true"] .wsw-chev { color: ${C.textHi}; }

        /* ── Menu ──────────────────────────────────────────────────────────── */
        .wsw-menu {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          min-width: 100%;
          width: max-content;
          max-width: 240px;
          background: ${C.surface};
          border: 1px solid ${C.border};
          border-radius: ${RADIUS.lg}px;
          box-shadow: ${SHADOW.panel};
          padding: 5px;
          z-index: 60;
          /* Never spill past the viewport bottom at high zoom — the trigger sits
             ~108px down the sidebar (wordmark above it), so subtract that plus a
             margin and let the list scroll. Same guarantee the shared popover
             layer gives. */
          max-height: calc(100vh - 130px);
          overflow-y: auto;
        }
        .wsw[data-collapsed="true"] .wsw-menu { left: 0; min-width: 210px; }
        .wsw-menu-label {
          font-size: ${T.tag}px;
          font-weight: ${W.medium};
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: ${C.textLo};
          padding: 5px 9px 6px;
        }

        .wsw-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 8px 9px;
          background: transparent;
          border: none;
          border-radius: ${RADIUS.md}px;
          cursor: pointer;
          text-align: left;
          font-family: ${FU};
          font-size: ${T.body}px;
          color: ${C.textMd};
          transition: background 120ms ease, color 120ms ease;
        }
        .wsw-item:hover { background: var(--tq-mute); color: ${C.textHi}; }
        /* Active workspace row = a hue-less neutral highlight (matches the
           uniform nav-selection treatment), NOT the workspace accent. The
           trigger's .wsw-avatar keeps its accent as workspace identity. */
        .wsw-item[data-active="true"] {
          color: ${C.textHi};
          font-weight: ${W.medium};
          background: color-mix(in srgb, ${C.textHi} 9%, transparent);
        }
        .wsw-item-ico { color: ${C.textLo}; flex-shrink: 0; }
        .wsw-item:hover .wsw-item-ico { color: ${C.textMd}; }
        .wsw-item[data-active="true"] .wsw-item-ico { color: ${C.textHi}; }
        .wsw-item-label { flex: 1; white-space: nowrap; }
        .wsw-soon {
          font-size: ${T.tag}px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: ${C.textLo};
          border: 1px solid ${C.border};
          border-radius: ${RADIUS.sm}px;
          padding: 1px 5px;
          flex-shrink: 0;
        }
        .wsw-check { color: ${C.textHi}; flex-shrink: 0; }
        .wsw-divider { height: 1px; background: ${C.border}; margin: 5px 4px; }
        .wsw-home { color: ${C.textMd}; }

        /* Account line + log out. The signed-in email is a quiet caption; log out
           mirrors the danger idiom on hover so it reads as the exit action. */
        .wsw-account {
          padding: 4px 9px 6px;
          font-size: ${T.tag}px;
          color: ${C.textLo};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 220px;
        }
        .wsw-account strong { color: ${C.textMd}; font-weight: ${W.medium}; }
        .wsw-logout { color: ${C.textMd}; }
        .wsw-logout:hover { background: color-mix(in srgb, var(--color-series-crisis) 10%, transparent); color: ${C.crisis}; }
        .wsw-logout:hover .wsw-item-ico { color: ${C.crisis}; }
      `}</style>
    </div>
  );
}
